import React from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { withStyles } from '@material-ui/core'
import { DragIndicator } from '@material-ui/icons'
import { DragSource } from 'react-dnd'
import classNames from 'classnames'
import { toggleTaskModal, expandFunnelStage } from 'actions/campaign/addOrEdit'
import { currentlyUsedTaskTypesByIDSelector } from 'selectors/tasks'
import EntityTypeIcon from 'utils/EntityTypeIcon'
import { SUCCESS } from 'utils/statusConstants'
import language from 'utils/vast-languagepack'
import ClickableTooltip from 'components/theme/ClickableTooltip'
import TooltipContent from 'components/A2P10DLC/TooltipContent'

const TaskType = DragSource(
  props => props.type,
  {
    beginDrag: props => {
      // props.expandFunnelStage(null)
      return { taskType: props.taskType }
    },
    canDrag: props => !props.disabled && props.draggable,
  },
  (connect, monitor) => ({
    connectDragSource: connect.dragSource(),
    connectDragPreview: connect.dragPreview(),
    isDragging: monitor.isDragging(),
  }),
)(
  ({
    taskType,
    label,
    Icon,
    classes,
    disabled,
    connectDragSource,
    connectDragPreview,
    isDropped,
    isDragging,
    toggleTaskModal,
    draggable,
  }) =>
    connectDragSource(
      <span
        id={`${taskType.id}-draggableCampaignPageTaskType`}
        style={{
          opacity: isDragging ? 0.4 : 1,
          borderRadius: 35,
          transform: 'translate(0, 0)',
        }}
        className={
          disabled
            ? classNames(classes.taskDisabled, 'draggableCampaignPageTaskType')
            : draggable
            ? classNames(classes.task, 'draggableCampaignPageTaskType')
            : classNames(
                classes.taskUnDraggable,
                'draggableCampaignPageTaskType',
              )
        }
        // onClick={() => toggleTaskModal(taskType)}
        title={taskType.display_name}
        data={taskType.id}
      >
        <span onClick={() => toggleTaskModal(taskType)}>
          {Icon}
          {label}
          <DragIndicator
            className={classNames(
              classes.taskIcon,
              disabled
                ? classes.dragIconDisabled
                : draggable
                ? classes.dragIcon
                : classes.dragIconUnDraggable,
            )}
          />
        </span>
      </span>,
    ),
)

export default connect(
  state => ({
    stepper: state.view.stepper.campaign,
    funnelStageExpanded: state.campaign.addOrEdit.funnelStageExpanded,
    taskTypes: currentlyUsedTaskTypesByIDSelector(state),
    taskTypesLoaded: state.loadStatus.taskTypes === SUCCESS,
    stagesLength: state.campaign.addOrEdit.campaign.stages.length,
    status: state.campaign.addOrEdit.campaign.status,
    viewSelected: state.tasks.viewSelected,
    approvedCampaignExists: Object.values(
      Object.values(state.data.a2p10dlc || {})?.[0]?.campaigns || {},
    ).some(campaign => campaign?.campaignStatus?.slice?.(0, 8) === 'VERIFIED'),
    vr_role: state.auth?.session?.idToken?.payload?.['custom:vr_role'],
    a2p10dlcExists: Object.values(state.data?.a2p10dlc || {})?.length,
  }),
  dispatch =>
    bindActionCreators({ toggleTaskModal, expandFunnelStage }, dispatch),
)(
  withStyles(theme => ({
    text: {
      color: theme.palette.secondary.main,
      marginBottom: 10,
      fontSize: 13,
    },
    section: { marginBottom: 10 },
    task: {
      cursor: 'grab',
      padding: '10px 0px 12px 10px',
      fontSize: 13,
      color: '#49466a',
      backgroundColor: '#d6e6f8',
      border: '2px solid #49466a',
      borderRadius: 35,
      display: 'inline-block',
      margin: 5,
      '&:hover': {
        backgroundColor: '#adbbed',
      },
    },
    taskUnDraggable: {
      padding: '10px 0px 12px 10px',
      fontSize: 13,
      color: '#49466a',
      backgroundColor: '#d6e6f8',
      border: '2px solid #49466a',
      borderRadius: 35,
      display: 'inline-block',
      margin: 5,
      '&:hover': {
        backgroundColor: '#adbbed',
      },
    },
    taskDisabled: {
      cursor: 'default',
      backgroundColor: '#edf5fe',
      color: '#787878',
      border: '2px solid #787878',
      padding: '10px 0px 12px 10px',
      fontSize: 13,
      borderRadius: 35,
      display: 'inline-block',
      margin: 5,
    },
    taskIcon: {
      marginRight: 5,
      marginBottom: '-7px',
      fontSize: 22,
    },
    dragIcon: {
      fill: '#8e95af',
      cursor: 'grab',
    },
    dragIconUnDraggable: {
      fill: '#8e95af',
    },
    dragIconDisabled: {
      cursor: 'default',
    },
  }))(
    ({
      classes,
      stepper,
      funnelStageExpanded,
      toggleTaskModal,
      taskTypes,
      taskTypesLoaded,
      stagesLength,
      expandFunnelStage,
      status,
      viewSelected,
      approvedCampaignExists,
    }) => {
      const disabled =
        status === 'L' || status === 'C' || (stepper === 1 && !stagesLength)

      const description =
        stepper === 1 ? (
          <div className={classes.text}>
            {language.campaign.add.taskPaperFunnel.taskDescription}{' '}
            <strong>
              {language.campaign.add.taskPaperFunnel.taskDescription2}
            </strong>{' '}
            {language.campaign.add.taskPaperFunnel.taskDescription3}{' '}
            <strong>
              {language.campaign.add.taskPaperFunnel.taskDescription4}
            </strong>{' '}
            {language.campaign.add.taskPaperFunnel.taskDescription5}{' '}
            <strong>
              {language.campaign.add.taskPaperFunnel.taskDescription6}
            </strong>{' '}
            {language.campaign.add.taskPaperFunnel.taskDescription7}
            <strong>
              {language.campaign.add.taskPaperFunnel.taskDescription8}
            </strong>{' '}
            {language.campaign.add.taskPaperFunnel.taskDescription9}
          </div>
        ) : viewSelected === 'list' ? (
          <div className={classes.text}>
            {language.campaign.add.taskPaperList.taskDescription0}{' '}
            <strong>
              {language.campaign.add.taskPaperList.taskDescription1}
            </strong>{' '}
            {language.campaign.add.taskPaperList.taskDescription2}{' '}
            <strong>
              {language.campaign.add.taskPaperList.taskDescription3}
            </strong>{' '}
            {language.campaign.add.taskPaperList.taskDescription4}
          </div>
        ) : (
          <div className={classes.text}>
            {language.campaign.add.taskPaper.taskDescription}{' '}
            <strong>{language.campaign.add.taskPaper.taskDescription2}</strong>{' '}
            {language.campaign.add.taskPaper.taskDescription3}{' '}
            <strong>{language.campaign.add.taskPaper.taskDescription4}</strong>{' '}
            {language.campaign.add.taskPaper.taskDescription5}{' '}
            <strong>{language.campaign.add.taskPaper.taskDescription6}</strong>{' '}
            {language.campaign.add.taskPaper.taskDescription7}
          </div>
        )

      return (
        <div>
          <div className={classes.section}>
            {description}
            <div
              style={{ marginLeft: '-5px' }}
              id="draggableCampaignPageTaskTypes"
            >
              {taskTypesLoaded
                ? Object.values(taskTypes).map(taskType => {
                    const a2p10dlcDisabled =
                      taskType.name === 'trigger.cmd.send.sms' &&
                      !approvedCampaignExists

                    const taskTypeComponent = (
                      <TaskType
                        draggable={stepper === 1 || viewSelected === 'calendar'}
                        key={`taskTypeCampaignPage${taskType.id}`}
                        type="taskType"
                        Icon={
                          <EntityTypeIcon
                            entityType="task"
                            entityID={taskType.id}
                            className={classes.taskIcon}
                          />
                        }
                        classes={classes}
                        disabled={disabled || a2p10dlcDisabled}
                        label={taskType.display_name}
                        taskType={taskType}
                        toggleTaskModal={taskType => {
                          return toggleTaskModal({
                            taskTypeID: taskType.id,
                            stageIdx: funnelStageExpanded,
                          })
                        }}
                        expandFunnelStage={expandFunnelStage}
                      />
                    )

                    return a2p10dlcDisabled ? (
                      <ClickableTooltip
                        key={`taskTypeCampaignPage${taskType.id}-tooltip`}
                        content={<TooltipContent />}
                      >
                        {taskTypeComponent}
                      </ClickableTooltip>
                    ) : (
                      taskTypeComponent
                    )
                  })
                : null}
            </div>
          </div>
          {/* <div style={{ marginTop: 20 }} className={classes.section}>
            <div className={classes.text}>Choose from your existing Tasks</div>
            <div style={{ marginLeft: '-5px' }}>{taskTypesMap(existingTasks)}</div>
          </div> */}
        </div>
      )
    },
  ),
)
